/**
 * Every screen in the check, in the order people walk them.
 *
 * This is the one place that holds the order. `src/routes/index.tsx` maps each
 * id to its component, and TypeScript makes that map exhaustive, so a view can
 * never exist here without a screen or drift out of sequence.
 *
 * `id` is the internal key — it is what `leads.furthest_step` has always
 * stored, so old rows stay comparable. `name` is the readable label posted to
 * the drop-off tracker; `id` rides along with it so re-wording a label cannot
 * silently split its history.
 */

export type View = { readonly id: string; readonly name: string };

export const VIEWS = [
  { id: "hook", name: "Intro · ₹20,000 reward offer" },
  { id: "capture", name: "Lead capture form" },
  { id: "changing", name: "Shift 1 · Hiring, 2019 → 2026" },
  { id: "m1", name: "Milestone 1 · ₹5,000 unlocked" },
  { id: "sec2", name: "Section 2 · What employers want" },
  { id: "profile", name: "About you · Quick profile" },
  { id: "news", name: "Market 1 · Where the jobs are opening" },
  { id: "interview", name: "Market 3 · The interview question" },
  { id: "answer", name: "Market 4 · Build your answer" },
  { id: "cvs", name: "Market 6 · Three CVs, one seat" },
  { id: "m2", name: "Milestone 2 · ₹10,000 unlocked" },
  { id: "proof", name: "CAIRA intro · Course or credential" },
  { id: "caira", name: "CAIRA 1 · What CAIRA is" },
  { id: "agents", name: "CAIRA 3 · Miles AI Labs" },
  { id: "myths", name: "CAIRA 4 · Three AI myths" },
  { id: "miles", name: "CAIRA 6 · Why Miles" },
  { id: "m3", name: "Milestone 3 · ₹15,000 unlocked" },
  { id: "rounds", name: "Check intro · Six games" },
  { id: "r1", name: "Game 1 · Machine in your office?" },
  { id: "r2", name: "Game 2 · Myth shredder" },
  { id: "r3", name: "Game 3 · Talk to Excel" },
  { id: "r4", name: "Game 4 · Trust or verify" },
  { id: "r5", name: "Game 5 · Right tool, right job" },
  { id: "r6", name: "Game 6 · Dashboard detective" },
  { id: "m4", name: "Milestone 4 · ₹20,000 unlocked" },
  { id: "verdict", name: "Verdict · Your score" },
  { id: "scratch", name: "Scratch · Reward reveal" },
  { id: "pricing", name: "Pricing · Reserve your seat" },
] as const satisfies readonly View[];

export type ViewId = (typeof VIEWS)[number]["id"];

export const VIEW_IDS: readonly string[] = VIEWS.map((v) => v.id);

/** Tagged on every Clarity session, so card numbers from different flows are never mixed. */
export const FLOW_VERSION = String(VIEWS.length);

/**
 * The 34-screen order live until 15 Oct 2026. Progress saved back then holds
 * only an index into this list, so it has to be read against it.
 */
const VIEWS_34 = [
  "hook", "capture", "changing", "versus", "teams", "m1",
  "sec2", "profile", "news", "jd", "interview", "answer", "salary", "cvs", "m2",
  "proof", "caira", "timeline", "agents", "myths", "voices", "miles", "m3",
  "rounds", "r1", "r2", "r3", "r4", "r5", "r6", "m4",
  "verdict", "scratch", "pricing",
];

const milestonesBefore = (ids: readonly string[], i: number) =>
  ids.slice(0, i).filter((id) => /^m\d$/.test(id)).length;

/**
 * Where saved progress resumes. New saves carry the view id (`at`). Older ones
 * carry only an index, which may be into the 34-screen flow: the milestones
 * already credited tell the two apart, since every milestone before the saved
 * screen is credited on arrival. A removed screen resumes at the next one kept.
 */
export function resumeIndex(saved: { i?: unknown; at?: unknown; credited?: unknown }): number {
  if (typeof saved.at === "string" && VIEW_IDS.includes(saved.at)) return VIEW_IDS.indexOf(saved.at);

  const i = typeof saved.i === "number" && Number.isInteger(saved.i) && saved.i > 0 ? saved.i : 0;
  if (!i) return 0;

  const credited =
    saved.credited && typeof saved.credited === "object" ? Object.keys(saved.credited).length : 0;
  // Credited can be one ahead when they had already dropped the coin on the milestone itself.
  const fits = (ids: readonly string[]) => {
    const before = milestonesBefore(ids, i);
    return credited === before || (credited === before + 1 && /^m\d$/.test(ids[i] ?? ""));
  };
  const legacy = i >= VIEWS.length || (!fits(VIEW_IDS) && fits(VIEWS_34));
  if (!legacy) return i;

  const kept = VIEWS_34.slice(i).find((id) => VIEW_IDS.includes(id));
  return kept ? VIEW_IDS.indexOf(kept) : VIEWS.length - 1;
}
